import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SubHeader } from "../../../../components/SubHeader";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { AdminPSACreateDrawer } from "./components/AdminPSACreateDrawer";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { adminActions } from "../../../../redux/admin/admin.slice";
import { adminSliceSelectors } from "../../../../redux/admin/admin.selector";
import { AtiraEmpty } from "../../../../components/AtiraEmpty";
import { AdminPSACard } from "./components/AdminPSACard";
import { PSA } from "../../../../model/admin/AdminPSA";
import { AdminPSAUpdateDrawer } from "./components/AdminPSAUpdateDrawer";
import { WarningModal } from "../../../../components/WarningModal";
import { AtiraToast } from "../../../../utils/AtiraToast";
import styled from "styled-components";
import { PageIcons } from "../../../../utils/GlobalIcons";

const Container = styled(Flex)`
  flex: 1;
  flex-direction: column;

  html[dir="rtl"] & .atira-subheader-title-container svg {
    transform: rotateY(180deg);
  }
`;

const AdminPSAUsers: React.FC = () => {
  const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [resendModalVisible, setResendModalVisible] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedPSA, setSelectedPSA] = useState<PSA | null>(null);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const psas = useAppSelector(adminSliceSelectors.selectPSAs);

  const onEdit = (psa: PSA) => {
    setSelectedPSA(psa);
    setEditDrawerVisible(true);
  };

  const onResend = (psa: PSA) => {
    setSelectedPSA(psa);
    setResendModalVisible(true);
  };

  const onDelete = (psa: PSA) => {
    setSelectedPSA(psa);
    setDeleteModalVisible(true);
  };

  const deletePSA = async () => {
    try {
      if (!selectedPSA) {
        return;
      }

      setDeleteLoading(true);

      await dispatch(
        adminActions.deletePSA({ psaId: selectedPSA._id, userId }),
      ).unwrap();
      await dispatch(adminActions.getPSAs({ userId })).unwrap();

      setDeleteModalVisible(false);

      AtiraToast.success(t("admin.psa.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteLoading(false);
    }
  };

  const resendPSA = async () => {
    try {
      if (!selectedPSA) {
        return;
      }

      setResendLoading(true);

      await dispatch(
        adminActions.resendPSA({ psaId: selectedPSA._id, userId }),
      ).unwrap();
      await dispatch(adminActions.getPSAs({ userId })).unwrap();

      setResendModalVisible(false);

      AtiraToast.success(t("admin.psa.resend.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    dispatch(adminActions.getPSAs({ userId }));
  }, [dispatch, userId]);

  return (
    <Container>
      <SubHeader
        title={t("admin.psa.widget.title")}
        icon={PageIcons.AdminPSAUsers}
        actions={[
          {
            title: t("common.create"),
            icon: faPlus,
            onClick: () => setCreateDrawerVisible(true),
          },
        ]}
      />

      <Flex
        flexDirection="column"
        gap="m"
        padding="m"
        paddingTop="none"
        flex={1}
      >
        <Text fontWeight={"bold"}>{t("admin.psa.route.title")}</Text>

        {psas?.length ? (
          psas.map((psa) => (
            <AdminPSACard
              key={psa._id}
              psa={psa}
              onEdit={() => onEdit(psa)}
              onDelete={() => onDelete(psa)}
              onResend={() => onResend(psa)}
            />
          ))
        ) : (
          <AtiraEmpty
            description={t("admin.psa.empty")}
            action={{
              title: t("common.create"),
              onClick: () => setCreateDrawerVisible(true),
            }}
          />
        )}
      </Flex>

      <AdminPSACreateDrawer
        open={createDrawerVisible}
        onClose={() => setCreateDrawerVisible(false)}
      />

      <AdminPSAUpdateDrawer
        open={editDrawerVisible}
        onClose={() => setEditDrawerVisible(false)}
        psa={selectedPSA}
      />

      <WarningModal
        title={t("admin.psa.delete.title")}
        description={t("admin.psa.delete.description")}
        isOpen={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={deletePSA}
        loading={deleteLoading}
      />

      <WarningModal
        title={t("admin.psa.resend.title")}
        description={t(
          `admin.psa.resend.${selectedPSA?.isPublic ? "public" : "targeted"}.description`,
        )}
        isOpen={resendModalVisible}
        onClose={() => setResendModalVisible(false)}
        onConfirm={resendPSA}
        loading={resendLoading}
      />
    </Container>
  );
};

export default AdminPSAUsers;
